import React, { CSSProperties, useMemo, useRef } from 'react';
import { Icon, IconNames } from 'src/app/shared/Icon/Icon';
import styles from './Drawer.module.scss';
import cn from 'classnames';
import { AtomVariant } from 'src/types/UserInterface';
import FocusTrap from 'focus-trap-react';
import { Transition, TransitionStatus } from 'react-transition-group';

interface DrawerProps {
  isOpen: boolean;
  onToggle: () => void;
  variant?: AtomVariant;
  icon?: Partial<{
    beforeOpen: IconNames;
    afterOpen: IconNames;
  }>
}

const duration = 300;

export const Drawer: React.FC<DrawerProps> = ({ isOpen, onToggle, icon, children, variant }) => {
  const drawerRef = useRef<HTMLDivElement>(null);
  const offset = 480;
  const toggleSize = 40;
  const transitions: Record<TransitionStatus, CSSProperties> = {
    entering: { marginLeft: 0, marginRight: 0 },
    entered:  { marginLeft: 0, marginRight: 0 },
    exiting:  { marginLeft: `-${offset}px`, marginRight: toggleSize },
    exited:  { marginLeft: `-${offset}px`, marginRight: toggleSize },
    unmounted:  { marginLeft: `-${offset}px`, marginRight: toggleSize },
  };

  const toggleIconValue: IconNames = useMemo(() => {
    return isOpen ? icon?.afterOpen ?? 'XIcon' : icon?.beforeOpen ?? 'MenuIcon';
  }, [icon, isOpen]);

  return (
    <Transition in={isOpen} timeout={duration} classNames="my-node" nodeRef={drawerRef} mountOnEnter>
      {(state) => {
        return (
          <FocusTrap focusTrapOptions={{ allowOutsideClick: true }} active={isOpen}>
            <aside
              ref={drawerRef}
              style={{ ...transitions[state], transitionDuration: `${duration}ms`, width: `${offset}px` }}
              className={cn(styles['o-drawer'], {
                [styles[`o-drawer--${variant}`]]: variant,
                [styles['is-open']]: isOpen,
              })}
            >
              <div className={cn(styles['o-drawer__content'], { [styles['is-visible']]: isOpen })}>
                {children}
              </div>
              <button
                className={styles['o-drawer__toggle']}
                type="button"
                onClick={onToggle}
                aria-label={isOpen ? 'Sluit menu' : 'Open menu'}
                aria-hidden={!isOpen}
                style={{ width: toggleSize, height: toggleSize }}
              >
                <Icon icon={toggleIconValue} />
              </button>
            </aside>
          </FocusTrap>
        );
      }}
    </Transition>
  );
};
